import { useEffect, useState, useCallback } from "react";
import { Settings, Save, RotateCcw, Info, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const FIELDS = [
  {
    key: "affiliate_commission_pct",
    label: "Commission Rate",
    suffix: "%",
    min: 0, max: 100, step: 0.5,
    desc: "Default % of player net loss credited to affiliate every cycle. Tier overrides still apply per affiliate.",
    safeRange: [20, 40],
  },
  {
    key: "affiliate_min_payout",
    label: "Min Payout Amount",
    suffix: "৳",
    min: 0, max: 100000, step: 50,
    desc: "Minimum amount an affiliate can request as payout. Lower = more frequent small payouts, higher = lower ops cost.",
    safeRange: [300, 2000],
  },
  {
    key: "payment_gateway_fee_pct",
    label: "Gateway Fee",
    suffix: "%",
    min: 0, max: 20, step: 0.1,
    desc: "Average payment gateway fee deducted from real deposits before net-loss calculation. Stops over-commissioning the gateway fee portion.",
    safeRange: [1, 5],
  },
  {
    key: "affiliate_wagering_ratio",
    label: "Wagering Ratio",
    suffix: "×",
    min: 0, max: 10, step: 0.1,
    desc: "Players must wager ≥ deposit × ratio for their loss to count. Set 0 to disable. Industry standard: 1.0×. Slot-heavy: 2.0×.",
    safeRange: [0.5, 2.0],
  },
];

function isInSafeRange(key, val) {
  const f = FIELDS.find((x) => x.key === key);
  if (!f) return true;
  return val >= f.safeRange[0] && val <= f.safeRange[1];
}

export default function AffiliateSafeConfig() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/safe-config");
      setData(data);
      setForm(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load config");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = data && Object.keys(form).some((k) => Number(form[k]) !== Number(data[k]) || form[k] !== data[k]);

  const save = async () => {
    if (!changed) return toast("No changes");
    setSaving(true);
    try {
      const diff = {};
      Object.keys(form).forEach((k) => {
        if (k === "affiliate_enabled" ? form[k] !== data[k] : Number(form[k]) !== Number(data[k])) {
          diff[k] = form[k];
        }
      });
      const { data: res } = await api.patch("/admin/safe-config", diff);
      toast.success("Safe formula config updated");
      setData(res.current);
      setForm(res.current);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const resetDefaults = () => {
    setForm({
      affiliate_commission_pct: 30,
      affiliate_min_payout: 500,
      payment_gateway_fee_pct: 2.0,
      affiliate_wagering_ratio: 1.0,
      affiliate_enabled: true,
    });
    toast("Defaults loaded — click Save to apply");
  };

  // Sample preview calculation
  const sampleNetLoss = 10000;
  const sampleGwFee = sampleNetLoss * (Number(form.payment_gateway_fee_pct || 0) / 100);
  const sampleEffDep = sampleNetLoss - sampleGwFee;
  const sampleCommission = sampleEffDep * (Number(form.affiliate_commission_pct || 0) / 100);

  if (loading) return <div className="text-white/60 p-6">Loading config…</div>;

  return (
    <div className="space-y-5" data-testid="page-safe-config">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Settings size={18} style={{ color: "#d4af37" }} />
            <h1 className="text-[20px] font-black text-white">Safe Formula Configuration</h1>
          </div>
          <div className="text-[12px] text-white/55">
            Tune the affiliate commission safe formula. Changes apply to NEXT cycle settlement; live preview also reflects immediately.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetDefaults} className="ap-btn-ghost" data-testid="btn-reset-defaults">
            <RotateCcw size={14} /> Reset to Defaults
          </button>
          <button onClick={save} disabled={!changed || saving} className="ap-btn-gold" data-testid="btn-save-config">
            <Save size={14} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Enable/Disable toggle */}
      <div className="ap-glass" style={{ padding: 16 }} data-testid="enable-toggle-row">
        <label className="flex items-center justify-between gap-3 cursor-pointer">
          <div>
            <div className="text-white font-bold mb-0.5">Affiliate Program Enabled</div>
            <div className="text-[11.5px] text-white/55">
              Master switch. When OFF, the cycle scheduler will skip all settlements (existing wallet balances stay safe).
            </div>
          </div>
          <input
            type="checkbox"
            checked={!!form.affiliate_enabled}
            onChange={(e) => setForm((f) => ({ ...f, affiliate_enabled: e.target.checked }))}
            className="w-5 h-5 accent-[#d4af37]"
            data-testid="toggle-affiliate-enabled"
          />
        </label>
      </div>

      {/* Numeric fields */}
      <div className="grid lg:grid-cols-2 gap-4">
        {FIELDS.map((f) => {
          const val = Number(form[f.key] ?? 0);
          const inSafe = isInSafeRange(f.key, val);
          return (
            <div key={f.key} className="ap-glass" style={{ padding: 18, border: inSafe ? "1px solid rgba(34,192,115,0.35)" : "1px solid rgba(244,180,49,0.45)" }} data-testid={`field-${f.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-white font-bold text-[13px]">{f.label}</div>
                  <div className="text-[10.5px] text-white/45 mt-0.5">
                    Safe range: {f.safeRange[0]}–{f.safeRange[1]} {f.suffix}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {inSafe ? (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(34,192,115,0.15)", color: "#22c073", border: "1px solid rgba(34,192,115,0.4)" }}>
                      <CheckCircle2 size={10} className="inline mr-1" />Safe
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full" style={{ background: "rgba(244,180,49,0.15)", color: "#fcd34d", border: "1px solid rgba(244,180,49,0.4)" }}>
                      <AlertTriangle size={10} className="inline mr-1" />Outside range
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <input
                  type="number"
                  value={val}
                  min={f.min} max={f.max} step={f.step}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: parseFloat(e.target.value) || 0 }))}
                  className="bg-black/40 border border-white/15 rounded-lg px-3 py-2 text-white font-bold text-[18px] w-32 focus:outline-none focus:border-[#d4af37]"
                  data-testid={`input-${f.key}`}
                />
                <span className="text-white/55 text-[14px] font-bold">{f.suffix}</span>
              </div>
              <input
                type="range"
                min={f.min} max={f.max} step={f.step}
                value={val}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: parseFloat(e.target.value) || 0 }))}
                className="w-full accent-[#d4af37]"
                data-testid={`slider-${f.key}`}
              />
              <div className="flex items-start gap-2 mt-2 text-[11px] text-white/60">
                <Info size={12} className="shrink-0 mt-0.5" style={{ color: "#d4af37" }} />
                <span>{f.desc}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Sample formula preview */}
      <div className="ap-glass" style={{ padding: 20, background: "linear-gradient(135deg, rgba(212,175,55,0.10), transparent 70%)", border: "1px solid rgba(212,175,55,0.45)" }} data-testid="sample-preview">
        <div className="text-white font-bold mb-3 flex items-center gap-2">
          <Info size={14} style={{ color: "#d4af37" }} /> Sample Calculation Preview
        </div>
        <div className="text-[12px] text-white/70 mb-3">
          সাম্পল player যিনি ৳ {sampleNetLoss.toLocaleString()} deposit করলেন এবং wagering pass করলেন:
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-[12px]">
          <div className="p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold">Sample Deposit</div>
            <div className="text-white font-bold text-[16px] mt-1">৳ {sampleNetLoss.toLocaleString()}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold">Gateway Fee ({form.payment_gateway_fee_pct}%)</div>
            <div className="text-white/80 font-bold text-[16px] mt-1">−৳ {sampleGwFee.toFixed(2)}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
            <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold">Effective Loss Base</div>
            <div className="text-white font-bold text-[16px] mt-1">৳ {sampleEffDep.toLocaleString()}</div>
          </div>
          <div className="p-3 rounded-lg" style={{ background: "rgba(34,192,115,0.12)", border: "1px solid rgba(34,192,115,0.4)" }}>
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#22c073" }}>Affiliate Commission ({form.affiliate_commission_pct}%)</div>
            <div className="font-black text-[20px] mt-1" style={{ color: "#22c073" }}>৳ {sampleCommission.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Help text */}
      <div className="text-[11px] text-white/45 leading-relaxed">
        <strong className="text-white/70">Note:</strong> এই config global level এ apply হয়। Per-affiliate commission % override (Tier system) আছে — সেটা admin Affiliate List থেকে individually set করা যায়। Safe formula এর অন্যান্য rules (bonus exclude, completed-only withdrawal, deficit carry-forward, fraud detection) যেহেতু integrity-critical — এগুলো code-level এ permanent।
      </div>
    </div>
  );
}
